import { cloneDeep } from 'lodash';
import React from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import { IImageLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import LabelAction from '@/components/label-action';
import { useLocaleStore } from '@/i18n/index';
import EffectItem, { EffectItemShowTitle } from '../components/effect-item/effect-item';
import { getDefaultGlowValue } from '../constant';
import { useActiveData } from '../hook';
import { getColorAlpha, transferColorOpacity } from '../utils';

const ImageGlowItem: React.FC<{
    funcStatTrack: Function;
    editor: Editor;
}> = ({ funcStatTrack, editor }) => {
    const { $t } = useLocaleStore();
    const { activeLayer, setActiveData } = useActiveData();
    if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) return null;

    const imageData = activeLayer.data as IImageLayerProps;
    const effect = imageData.effect;
    const doFuncStatTrack = () => {
        funcStatTrack({
            useId: 49,
            downloadId: 98,
            clickId: 49,
        });
    };
    const addGlow = () => {
        if (!imageData.effect) {
            imageData.effect = {
                effectAlpha: 1,
                glow: [getDefaultGlowValue()],
                shadow: [],
                stroke: [],
                screenshotUrl: '',
            };
        } else {
            imageData.effect.glow = [getDefaultGlowValue()];
        }
        const newLayer = cloneDeep(activeLayer);
        editor.getInstance(ImageLayer).changeImageEffectAttr(newLayer, undefined, 'effectGlow');
        setActiveData(newLayer);
        doFuncStatTrack();
    };

    if (!effect || !effect.glow || effect.glow.length === 0) {
        return (
            <EffectItem
                title={$t((v) => v.imageEffectPanel.imageGlowItem.outGlow)}
                iconName="ContourBold"
                handleClick={() => {
                    addGlow();
                }}
            />
        );
    }

    const glow = effect.glow[0];

    const updateData = (key: string, value: any, isEnd: boolean, stopUpdate = false) => {
        if (isEnd) {
            doFuncStatTrack();
        }
        if (stopUpdate || !imageData.effect) {
            return;
        }
        imageData.effect.glow[0][key] = value;
        const newLayer = cloneDeep(activeLayer);
        editor.getInstance(ImageLayer).changeImageEffectAttr(newLayer, undefined, 'effectGlow');
        setActiveData(newLayer);
    };

    const visibleClick = () => {
        const originVisible = glow.hidden;
        updateData('hidden', !originVisible, true, false);
    };

    const delClick = () => {
        if (!imageData.effect) {
            return;
        }
        imageData.effect.glow = [];
        const newLayer = cloneDeep(activeLayer);
        editor.getInstance(ImageLayer).changeImageEffectAttr(newLayer, undefined, 'effectGlow');
        setActiveData(newLayer);
        doFuncStatTrack();
    };

    const colorChange = (color: string, isEnd = false) => {
        updateData('color', color, isEnd, isEnd);
    };

    const widthChange = (width: number, isEnd = false) => {
        updateData('width', width, isEnd, isEnd);
    };

    const alphaChange = (alpha: number, isEnd = false) => {
        updateData('color', transferColorOpacity(glow.color, alpha), isEnd, isEnd);
    };

    return (
        <div className="c-image-effect-item-content">
            <EffectItemShowTitle
                title={$t((v) => v.imageEffectPanel.imageGlowItem.outGlow)}
                iconName="ContourBold"
                handleVisibleClick={visibleClick}
                handleDelClick={delClick}
                visible={!glow.hidden}
            />

            <div className="content">
                <LabelAction
                    title={$t((v) => v.imageEffectPanel.imageGlowItem.color)}
                    align
                    style={{ marginBottom: '8px' }}
                >
                    <ColorGroup
                        popupTriggerSize={{
                            width: 58,
                            height: 26,
                        }}
                        placement="topLeft"
                        color={glow.color}
                        setColor={(val) => {
                            colorChange(val);
                        }}
                        popupOverlayClassName="c-markup-panel__color-picker"
                        setColorAfter={(val, _, { isBrandColor }) => {
                            colorChange(val, true);
                            if (isBrandColor) {
                                window._stat_track?.('hb_layers_func_use', {
                                    func_id: '9_107',
                                });
                            }
                        }}
                        showIcon={false}
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                window._stat_track?.('hb_layers_func_click', {
                                    func_id: '9_107',
                                });
                            }
                        }}
                    />
                </LabelAction>

                <LabelAction title={$t((v) => v.imageEffectPanel.imageGlowItem.scope)} align>
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <SliderInput
                            min={0}
                            max={100}
                            value={glow.width * 100}
                            precision={1}
                            showStepBtn
                            type="horizontal"
                            onChange={(val: number) => {
                                widthChange(val / 100);
                            }}
                            onAfterChange={(val: number) => {
                                widthChange(val / 100, true);
                            }}
                            onClick={() => {}}
                        />
                    </div>
                </LabelAction>

                <LabelAction title={$t((v) => v.imageEffectPanel.imageGlowItem.opacity)} align>
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <SliderInput
                            min={0}
                            max={100}
                            value={getColorAlpha(glow.color)}
                            precision={1}
                            showStepBtn
                            type="horizontal"
                            onChange={(val: number) => {
                                alphaChange(val);
                            }}
                            onAfterChange={(val: number) => {
                                alphaChange(val, true);
                            }}
                            onClick={() => {}}
                        />
                    </div>
                </LabelAction>
            </div>
        </div>
    );
};

export default ImageGlowItem;
